<script setup>
/**
 * Header bell: unread badge + a popover list of the caller's Frappe notifications.
 *
 * Unread count is fetched on mount and refreshed by the socket `notification`
 * event (published by Notification Log.after_insert), on window focus, and by a
 * slow poll as a fallback when the socket is down.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { ErrorMessage, Tabs } from 'frappe-ui'
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui'
import { rpc, errorMessage } from '../api'
import { session } from '../session'
import { groupNotifications, relativeTime } from '../utils/notifications'

const emit = defineEmits(['open-settings'])

const API = 'church_erp.church_erp.utils.notifications_api'
const PAGE = 20
const POLL_MS = 60000

const open = ref(false)
const tab = ref('all')
const items = ref([])
const unread = ref(0)
const loading = ref(false)
const error = ref('')
const hasMore = ref(false)
const start = ref(0)

let pollTimer = null
let socket = null
let disposed = false

const tabs = [{ label: 'All' }, { label: 'Unread' }]
const badge = computed(() => (unread.value > 9 ? '9+' : String(unread.value)))
const showBadge = computed(() => unread.value > 0)

const groups = computed(() => groupNotifications(items.value))

async function refreshUnread() {
  try {
    const data = await rpc(`${API}.notifications_summary`)
    if (!disposed) unread.value = Number(data?.unread_count || 0)
  } catch {
    /* the badge is non-critical; keep the last known count */
  }
}

async function fetchList({ reset = true } = {}) {
  loading.value = true
  error.value = ''
  const from = reset ? 0 : start.value
  try {
    const data = await rpc(`${API}.list_notifications`, {
      tab: tab.value,
      start: from,
      page_length: PAGE,
    })
    const rows = Array.isArray(data?.notifications) ? data.notifications : []
    items.value = reset ? rows : [...items.value, ...rows]
    unread.value = Number(data?.unread_count || 0)
    hasMore.value = Boolean(data?.has_more)
    start.value = from + rows.length
  } catch (e) {
    error.value = errorMessage(e)
    if (reset) items.value = []
  } finally {
    loading.value = false
  }
}

function onOpenChange(value) {
  open.value = value
  if (value) {
    tab.value = 'all'
    fetchList({ reset: true })
    refreshUnread()
  }
}

function changeTab(value) {
  const next = typeof value === 'string' ? value.toLowerCase() : value === 1 ? 'unread' : 'all'
  if (next === tab.value) return
  tab.value = next
  fetchList({ reset: true })
}

async function markAllRead() {
  if (!unread.value) return
  const previous = items.value.map((i) => i.read)
  items.value = items.value.map((i) => ({ ...i, read: true }))
  unread.value = 0
  try {
    await rpc(`${API}.mark_all_notifications_read`)
    if (tab.value === 'unread') fetchList({ reset: true })
  } catch (e) {
    items.value = items.value.map((i, idx) => ({ ...i, read: previous[idx] }))
    await refreshUnread()
    error.value = errorMessage(e)
  }
}

async function openItem(item) {
  // optimistic: the dot disappears immediately, the server call can still fail
  if (!item.read) {
    item.read = true
    unread.value = Math.max(0, unread.value - 1)
    try {
      await rpc(`${API}.mark_notification_read`, { name: item.name })
    } catch {
      item.read = false
      unread.value += 1
    }
  }
  open.value = false
  if (item.route) window.location.assign(item.route)
}

function goAll() {
  open.value = false
  window.location.assign('/notifications')
}

function onRealtime() {
  refreshUnread()
  if (open.value) fetchList({ reset: true })
}

function onFocus() {
  if (document.visibilityState === 'visible') refreshUnread()
}

onMounted(() => {
  refreshUnread()
  socket = window.__church_socket
  if (socket?.on) socket.on('notification', onRealtime)
  pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') refreshUnread()
  }, POLL_MS)
  document.addEventListener('visibilitychange', onFocus)
  window.addEventListener('focus', onFocus)
})

onUnmounted(() => {
  disposed = true
  if (socket?.off) socket.off('notification', onRealtime)
  if (pollTimer) clearInterval(pollTimer)
  document.removeEventListener('visibilitychange', onFocus)
  window.removeEventListener('focus', onFocus)
})

watch(() => session.boot?.user, refreshUnread)

defineExpose({ refreshUnread })
</script>

<template>
  <PopoverRoot v-model:open="open" @update:open="onOpenChange">
    <PopoverTrigger as-child>
      <button
        type="button"
        class="relative inline-flex size-8 items-center justify-center rounded text-ink-gray-7 transition hover:bg-surface-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-gray-4"
        :aria-label="showBadge ? `Notifications, ${unread} unread` : 'Notifications'"
        aria-haspopup="dialog"
      >
        <span class="lucide-bell size-4" aria-hidden="true" />
        <span
          v-if="showBadge"
          class="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-surface-red-6 px-1 text-[10px] font-semibold leading-none text-white"
          data-testid="notification-badge"
        >
          {{ badge }}
        </span>
      </button>
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        align="end"
        :side-offset="8"
        class="z-[120] w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-outline-gray-1 bg-surface-elevation-2 shadow-xl outline-none"
        data-testid="notification-panel"
      >
        <div class="flex items-center justify-between gap-2 border-b border-outline-gray-1 px-3 py-2">
          <div class="text-sm font-semibold text-ink-gray-9">Notifications</div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="rounded px-2 py-1 text-xs text-ink-gray-6 transition hover:bg-surface-gray-2 disabled:opacity-50"
              :disabled="!unread"
              @click="markAllRead"
            >
              Mark all as read
            </button>
            <button
              type="button"
              class="inline-flex size-7 items-center justify-center rounded text-ink-gray-6 transition hover:bg-surface-gray-2"
              aria-label="Notification settings"
              @click="emit('open-settings'); open = false"
            >
              <span class="lucide-settings-2 size-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div class="border-b border-outline-gray-1 px-3 py-2">
          <Tabs :model-value="tab === 'unread' ? 1 : 0" :tabs="tabs" @update:model-value="changeTab" />
        </div>

        <div class="max-h-[70vh] overflow-y-auto" data-testid="notification-list">
          <div v-if="loading && !items.length" class="space-y-2 p-3" aria-hidden="true">
            <div v-for="n in 4" :key="n" class="flex gap-2">
              <div class="size-8 shrink-0 animate-pulse rounded-full bg-surface-gray-3" />
              <div class="flex-1 space-y-1.5">
                <div class="h-3 w-2/3 animate-pulse rounded bg-surface-gray-3" />
                <div class="h-3 w-full animate-pulse rounded bg-surface-gray-2" />
              </div>
            </div>
          </div>

          <div
            v-else-if="!items.length"
            class="flex flex-col items-center gap-2 px-4 py-10 text-center"
            data-testid="notification-empty"
          >
            <span class="lucide-bell-off size-6 text-ink-gray-4" aria-hidden="true" />
            <div class="text-sm font-medium text-ink-gray-7">You're all caught up</div>
            <div class="text-xs text-ink-gray-5">New giving, pledges and parish updates land here.</div>
          </div>

          <template v-else>
            <div v-for="group in groups" :key="group.label">
              <div class="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-ink-gray-5">
                {{ group.label }}
              </div>
              <button
                v-for="item in group.items"
                :key="item.name"
                type="button"
                class="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-surface-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-outline-gray-4"
                :class="item.read ? '' : 'bg-surface-gray-1'"
                @click="openItem(item)"
              >
                <span class="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-gray-2 text-ink-gray-6">
                  <span :class="[item.icon, 'size-4']" aria-hidden="true" />
                </span>
                <span class="min-w-0 flex-1">
                  <span class="flex items-start gap-2">
                    <span class="min-w-0 flex-1 truncate text-sm font-medium text-ink-gray-9">{{ item.title }}</span>
                    <span
                      v-if="!item.read"
                      class="mt-1.5 size-2 shrink-0 rounded-full bg-surface-blue-6"
                      aria-label="Unread"
                    />
                  </span>
                  <span v-if="item.description" class="mt-0.5 line-clamp-2 text-xs text-ink-gray-6">
                    {{ item.description }}
                  </span>
                  <span class="mt-1 block text-[11px] text-ink-gray-5">{{ relativeTime(item.created_at) }}</span>
                </span>
              </button>
            </div>
            <div v-if="hasMore" class="border-t border-outline-gray-1 p-2">
              <button
                type="button"
                class="w-full rounded py-1.5 text-xs font-medium text-ink-gray-7 transition hover:bg-surface-gray-2"
                :disabled="loading"
                @click="fetchList({ reset: false })"
              >
                Load more
              </button>
            </div>
          </template>
        </div>

        <ErrorMessage v-if="error" class="px-3 pb-2" :message="error" />

        <div class="border-t border-outline-gray-1 p-2">
          <button
            type="button"
            class="w-full rounded py-1.5 text-center text-xs font-medium text-ink-gray-7 transition hover:bg-surface-gray-2"
            @click="goAll"
          >
            View all notifications
          </button>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
